import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useAuth } from '../hooks/useAuth';
import { useUserData, clearUserCache } from '../hooks/useUserData';
import Layout from '../components/layout/Layout';
import { Button, Avatar } from '../components/ui';
import PremiumName from '../components/ui/PremiumName';
import EditProfileModal from '../components/profile/EditProfileModal';
import { PostCard, MasonryGrid } from '../components/posts';
import { ServiceCard, ServiceModal, CreateServiceModal } from '../components/services';
import { Portfolio } from '../components/portfolio';
import { FollowListModal } from '../components/users';
import { getUserPosts, getUserServices, getUserLikedPosts } from '../services/postService';
import { followUser, unfollowUser, checkIfFollowing, checkIfBlocked, blockUser, unblockUser } from '../services/userService';
import { getSavedPosts, getSavedServices } from '../services/savedService';

const ProfilePage = () => {
  const { userId } = useParams();
  const { userData: currentUserData, currentUser } = useAuth();
  const { userData: profileUserData } = useUserData(userId);
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateServiceModal, setShowCreateServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [userServices, setUserServices] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingServices, setLoadingServices] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [hasMoreServices, setHasMoreServices] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [lastVisibleService, setLastVisibleService] = useState(null);
  const [activeTab, setActiveTab] = useState('posts'); // posts | services | portfolio | likes | saved
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [showFollowListModal, setShowFollowListModal] = useState(false);
  const [followListType, setFollowListType] = useState('followers'); // 'followers' | 'following'
  const [likedPosts, setLikedPosts] = useState([]);
  const [loadingLiked, setLoadingLiked] = useState(false);
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedServices, setSavedServices] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedError, setSavedError] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  const navigate = useNavigate();
  const isOwnProfile = currentUserData?.id === userId;
  const displayUserData = isOwnProfile ? currentUserData : profileUserData;

  // Banner por defecto si no hay
  const defaultBanner = 'https://images.unsplash.com/photo-1557683316-973673baf926?w=1200&h=300&fit=crop';

  // Cargar posts del usuario
  useEffect(() => {
    const loadUserPosts = async () => {
      if (!userId) return;

      setLoadingPosts(true);
      setUserPosts([]); // Reset posts cuando cambia el usuario
      const result = await getUserPosts(userId, 50);
      
      if (result.success) {
        setUserPosts(result.posts);
        setHasMore(result.hasMore);
        setLastVisible(result.lastVisible);
      }
      
      setLoadingPosts(false);
    };

    loadUserPosts();
  }, [userId]);

  // Cargar servicios del usuario
  useEffect(() => {
    const loadUserServices = async () => {
      if (!userId) return;

      setLoadingServices(true);
      setUserServices([]); // Reset servicios cuando cambia el usuario
      const result = await getUserServices(userId, 40);
      
      if (result.success) {
        setUserServices(result.services);
        setHasMoreServices(result.hasMore);
        setLastVisibleService(result.lastVisible);
      }
      
      setLoadingServices(false);
    };

    loadUserServices();
  }, [userId]);

  // Verificar estado de seguimiento
  useEffect(() => {
    const checkFollowStatus = async () => {
      if (currentUser && userId && !isOwnProfile) {
        const isFollowingUser = await checkIfFollowing(currentUser.uid, userId);
        setIsFollowing(isFollowingUser);
      }
    };

    checkFollowStatus();
  }, [currentUser, userId, isOwnProfile]);

  // Cargar posts con like (solo en propio perfil, tab Me gusta)
  useEffect(() => {
    const loadLikedPosts = async () => {
      if (!currentUser?.uid || !isOwnProfile) return;
      setLoadingLiked(true);
      const result = await getUserLikedPosts(currentUser.uid, 50);
      if (result.success && result.posts) setLikedPosts(result.posts);
      setLoadingLiked(false);
    };
    loadLikedPosts();
  }, [currentUser?.uid, isOwnProfile]);

  // Verificar si el usuario actual tiene bloqueado a este perfil
  useEffect(() => {
    const loadBlocked = async () => {
      if (!currentUser?.uid || !userId || isOwnProfile) return;
      const blocked = await checkIfBlocked(currentUser.uid, userId);
      setIsBlocked(blocked);
    };
    loadBlocked();
  }, [currentUser?.uid, userId, isOwnProfile]);

  // Cargar guardados cuando se muestra la tab Guardados (solo propio perfil)
  useEffect(() => {
    const loadSaved = async () => {
      if (activeTab !== 'saved' || !userId || !isOwnProfile || !currentUser?.uid) return;
      setLoadingSaved(true);
      setSavedError(null);
      try {
        const [postsRes, servicesRes] = await Promise.all([
          getSavedPosts(userId),
          getSavedServices(userId)
        ]);
        if (postsRes.success && servicesRes.success) {
          setSavedPosts(postsRes.posts || []);
          setSavedServices(servicesRes.services || []);
        } else {
          setSavedError(postsRes.error || servicesRes.error || 'No se pudieron cargar los guardados');
        }
      } catch (err) {
        setSavedError(err.message || 'No se pudieron cargar los guardados');
      } finally {
        setLoadingSaved(false);
      }
    };
    loadSaved();
  }, [activeTab, userId, isOwnProfile, currentUser?.uid]);

  // Handler para seguir/dejar de seguir
  const handleFollowToggle = async () => {
    if (isFollowLoading || !currentUser) return;
    
    setIsFollowLoading(true);
    
    try {
      if (isFollowing) {
        const result = await unfollowUser(userId);
        if (result.success) {
          setIsFollowing(false);
          // Limpiar cache para refrescar datos
          clearUserCache(userId);
        } else {
          alert(result.error || 'Error al dejar de seguir');
        }
      } else {
        const result = await followUser(userId);
        if (result.success) {
          setIsFollowing(true);
          // Limpiar cache para refrescar datos
          clearUserCache(userId);
        } else {
          alert(result.error || 'Error al seguir');
        }
      }
    } catch (error) {
      console.error('Error en handleFollowToggle:', error);
      alert('Ocurrió un error. Por favor intenta de nuevo.');
    } finally {
      setIsFollowLoading(false);
    }
  };

  // Handlers para mostrar modales de seguidores/siguiendo
  const handleShowFollowers = () => {
    setFollowListType('followers');
    setShowFollowListModal(true);
  };

  const handleShowFollowing = () => {
    setFollowListType('following');
    setShowFollowListModal(true);
  };

  // Cargar más posts
  const loadMore = async () => {
    if (!hasMore || loadingPosts || !userId) return;

    setLoadingPosts(true);
    const result = await getUserPosts(userId, 50, lastVisible);
    
    if (result.success) {
      setUserPosts(prev => [...prev, ...result.posts]);
      setHasMore(result.hasMore);
      setLastVisible(result.lastVisible);
    }
    
    setLoadingPosts(false);
  };

  // Cargar más servicios
  const loadMoreServices = async () => {
    if (!hasMoreServices || loadingServices || !userId) return;

    setLoadingServices(true);
    const result = await getUserServices(userId, 40, lastVisibleService);
    
    if (result.success) {
      setUserServices(prev => [...prev, ...result.services]);
      setHasMoreServices(result.hasMore);
      setLastVisibleService(result.lastVisible);
    }
    
    setLoadingServices(false);
  };

  const handleServiceCreated = (newService) => {
    setUserServices(prevServices => [newService, ...prevServices]);
    setShowCreateServiceModal(false);
  };

  const handleServiceDeleted = (serviceId) => {
    setUserServices(prevServices => prevServices.filter(s => s.id !== serviceId));
    setSavedServices(prev => prev.filter(s => s.id !== serviceId));
    setSelectedService(null);
  };

  const handleServiceUpdated = (updatedService) => {
    setUserServices(prevServices => 
      prevServices.map(s => s.id === updatedService.id ? { ...s, ...updatedService } : s)
    );
    setSelectedService(prev => prev ? { ...prev, ...updatedService } : null);
  };

  if (!displayUserData) {
    return (
      <Layout>
        <div className="container-custom py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div>
            <p className="text-gray-600 dark:text-gray-400">
              Cargando perfil...
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  // Vista reducida cuando el usuario actual tiene bloqueado a este perfil
  if (!isOwnProfile && isBlocked) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
          <div className="container-custom py-12">
            <div className="max-w-md mx-auto text-center bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-8">
              <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Has bloqueado a este usuario</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                No puedes ver el perfil de {displayUserData.displayName || 'este usuario'}.
              </p>
              <Button
                variant="outline"
                onClick={async () => {
                  setBlockLoading(true);
                  const result = await unblockUser(userId);
                  if (result.success) setIsBlocked(false);
                  else alert(result.error || 'Error al desbloquear');
                  setBlockLoading(false);
                }}
                loading={blockLoading}
                disabled={blockLoading}
              >
                Desbloquear usuario
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const hasProfileBackground = displayUserData?.profileBackgroundURL && displayUserData?.isPremium;
  const profileBgType = displayUserData?.profileBackgroundType;
  const profileBgURL = displayUserData?.profileBackgroundURL;

  return (
    <Layout>
      <div className="min-h-screen relative">
        {/* Fondo de perfil (Premium) */}
        {hasProfileBackground && profileBgURL && (
          <div className="fixed inset-0 z-0 overflow-hidden">
            <div className="absolute inset-0 bg-black/60 z-[1]" aria-hidden />
            {profileBgType === 'video' ? (
              <video
                src={profileBgURL}
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 w-full h-full object-cover opacity-30"
                aria-hidden
              />
            ) : (
              <img
                src={profileBgURL}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-25"
                aria-hidden
              />
            )}
          </div>
        )}
        {!hasProfileBackground && (
          <div className="fixed inset-0 z-0 bg-gray-50 dark:bg-slate-950" aria-hidden />
        )}

        <div className="relative z-10 min-h-screen">
        {/* Banner Section */}
        <div className="relative">
          {/* Banner Image */}
          <div className="h-48 sm:h-64 lg:h-80 w-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 dark:from-indigo-900 dark:via-purple-900 dark:to-pink-900 relative overflow-hidden">
            {displayUserData.bannerURL ? (
              <img
                src={displayUserData.bannerURL}
                alt="Banner"
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={defaultBanner}
                alt="Banner por defecto"
                className="w-full h-full object-cover opacity-60"
              />
            )}
            
            {/* Edit Banner Button (solo si es tu perfil) */}
            {isOwnProfile && (
              <button
                onClick={() => setShowEditModal(true)}
                className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full transition-all"
                title="Editar perfil"
              >
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            )}
          </div>

          {/* Profile Info Container */}
          <div className="container-custom">
            <div className="relative">
              {/* Avatar - Superpuesto sobre el banner */}
              <div className="absolute top-2 left-4 sm:left-8">
                <div className="relative w-24 h-24">
                  {/* Wrapper con ring para evitar cuadrado */}
                  <div className="absolute inset-0 rounded-full ring-4 ring-white dark:ring-slate-950 shadow-xl overflow-hidden">
                    <Avatar
                      src={displayUserData.photoURL}
                      alt={displayUserData.displayName}
                      fallbackText={displayUserData.displayName}
                      size="3xl"
                    />
                  </div>
                </div>
              </div>

              {/* User Info: fila compacta avatar + nombre/handle + botón */}
              <div className="px-4 sm:px-8 pb-6">
                <div className="flex items-center gap-3 pt-2">
                  <div className="w-24 h-24 flex-shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <PremiumName
                        name={displayUserData.displayName || 'Usuario'}
                        isPremium={displayUserData.isPremium}
                        premiumColor={displayUserData.premiumNameColor}
                        nameClassName="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white"
                      />
                      {(displayUserData.isAmbassador || displayUserData.verified) && (
                        <svg className={`w-5 h-5 flex-shrink-0 ${displayUserData.isAmbassador ? 'text-violet-500' : 'text-blue-500'}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5 truncate">
                      @{displayUserData.username || displayUserData.email?.split('@')[0]}
                    </p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    {isOwnProfile ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowEditModal(true)}
                        icon={
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        }
                      >
                        Editar Perfil
                      </Button>
                    ) : (
                  <div className="flex gap-2 items-center">
                    <Button
                      variant={isFollowing ? "outline" : "gradient"}
                      size="sm"
                      onClick={handleFollowToggle}
                      loading={isFollowLoading}
                      disabled={isFollowLoading}
                      icon={
                        isFollowing ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                          </svg>
                        )
                      }
                    >
                      {isFollowing ? 'Siguiendo' : 'Seguir'}
                    </Button>
                    <Menu as="div" className="relative">
                      <MenuButton
                        as={Button}
                        variant="outline"
                        size="sm"
                        className="!p-2"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </MenuButton>
                      <MenuItems className="absolute right-0 mt-2 w-48 origin-top-right bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl py-1 focus:outline-none z-50">
                        <MenuItem>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => {
                                const url = `${window.location.origin}/profile/${userId}`;
                                navigator.clipboard.writeText(url).then(() => {
                                  alert('Enlace del perfil copiado al portapapeles');
                                }).catch(() => {
                                  alert('No se pudo copiar el enlace');
                                });
                              }}
                              className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm text-left ${
                                active ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                              </svg>
                              Compartir perfil
                            </button>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ active }) => (
                            <button
                              type="button"
                              disabled={blockLoading}
                              onClick={async () => {
                                if (isBlocked) {
                                  setBlockLoading(true);
                                  const result = await unblockUser(userId);
                                  if (result.success) setIsBlocked(false);
                                  else alert(result.error || 'Error al desbloquear');
                                  setBlockLoading(false);
                                } else {
                                  const name = displayUserData?.displayName || displayUserData?.username || 'este usuario';
                                  if (!window.confirm(`¿Bloquear a ${name}? No podrá ver tu perfil ni enviarte mensajes.`)) return;
                                  setBlockLoading(true);
                                  const result = await blockUser(userId);
                                  if (result.success) {
                                    setIsBlocked(true);
                                    navigate('/');
                                  } else {
                                    alert(result.error || 'Error al bloquear');
                                  }
                                  setBlockLoading(false);
                                }
                              }}
                              className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm text-left ${
                                active ? 'bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                              } ${isBlocked ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                            >
                              {isBlocked ? (
                                <>
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                  </svg>
                                  Desbloquear usuario
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                  </svg>
                                  Bloquear usuario
                                </>
                              )}
                            </button>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  </div>
                )}
                  </div>
                </div>
              </div>

              {/* Bio, badges y stats */}
              <div className="px-4 sm:px-8 pb-6 pt-0 -mt-2">
                <div className="pt-3">
                  {displayUserData.bio && (
                    <p className="text-gray-700 dark:text-gray-300 text-sm mb-3 max-w-2xl line-clamp-3">
                      {displayUserData.bio}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mb-3">
                      {displayUserData.isCreator && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                          Creador
                        </span>
                      )}
                      
                      {displayUserData.location && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {displayUserData.location}
                        </span>
                      )}

                      {displayUserData.createdAt && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Miembro desde {new Date(displayUserData.createdAt.seconds * 1000).toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-6 text-sm">
                      <button className="hover:underline">
                        <span className="font-bold text-gray-900 dark:text-white">{userPosts.length}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-1">Posts</span>
                      </button>
                      <button 
                        className="hover:underline"
                        onClick={handleShowFollowers}
                      >
                        <span className="font-bold text-gray-900 dark:text-white">{displayUserData.followersCount || 0}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-1">Seguidores</span>
                      </button>
                      <button 
                        className="hover:underline"
                        onClick={handleShowFollowing}
                      >
                        <span className="font-bold text-gray-900 dark:text-white">{displayUserData.followingCount || 0}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-1">Siguiendo</span>
                      </button>
                    </div>
                  </div>
              </div>

              {/* Tabs Navigation */}
              <div className="border-b border-gray-200 dark:border-slate-800 px-4 sm:px-8 min-w-0">
                <div className="flex gap-4 sm:gap-8 flex-nowrap pb-2">
                  <button
                    onClick={() => setActiveTab('posts')}
                    className={`flex-1 min-w-0 flex justify-center sm:flex-initial sm:flex-none py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'posts'
                        ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="hidden sm:inline">Posts</span>
                    </div>
                  </button>

                  <button
                    onClick={() => setActiveTab('services')}
                    className={`flex-1 min-w-0 flex justify-center sm:flex-initial sm:flex-none py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'services'
                        ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="hidden sm:inline">Servicios</span>
                    </div>
                  </button>

                  <button
                    onClick={() => setActiveTab('portfolio')}
                    className={`flex-1 min-w-0 flex justify-center sm:flex-initial sm:flex-none py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'portfolio'
                        ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="hidden sm:inline">Portfolio</span>
                    </div>
                  </button>

                  {isOwnProfile && (
                    <>
                      <button
                        onClick={() => setActiveTab('likes')}
                        className={`flex-1 min-w-0 flex justify-center sm:flex-initial sm:flex-none py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                          activeTab === 'likes'
                            ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                          <span className="hidden sm:inline">Me gusta</span>
                        </div>
                      </button>

                      <button
                        onClick={() => setActiveTab('saved')}
                        className={`flex-1 min-w-0 flex justify-center sm:flex-initial sm:flex-none py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                          activeTab === 'saved'
                            ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                          </svg>
                          <span className="hidden sm:inline">Guardados</span>
                        </div>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="container-custom py-6">{activeTab === 'posts' && (
            <div>

              {/* Loading */}
              {loadingPosts && userPosts.length === 0 && (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Cargando posts...</p>
                </div>
              )}

              {/* Posts Grid */}
              {!loadingPosts && userPosts.length > 0 && (
                <>
                  <MasonryGrid>
                    {userPosts.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                      />
                    ))}
                  </MasonryGrid>

                  {/* Botón cargar más */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <Button
                        variant="outline"
                        onClick={loadMore}
                        loading={loadingPosts}
                      >
                        Cargar más posts
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Empty state */}
              {!loadingPosts && userPosts.length === 0 && (
                <div className="text-center py-16">
                  <div className="max-w-md mx-auto">
                    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {isOwnProfile ? 'No tienes posts todavía' : 'No hay posts para mostrar'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      {isOwnProfile 
                        ? 'Comparte tu arte y creatividad con la comunidad' 
                        : 'Este usuario aún no ha publicado nada'}
                    </p>
                    {isOwnProfile && (
                      <Link to="/">
                        <Button variant="gradient">
                          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Crear tu primer post
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              {/* Loading */}
              {loadingServices && userServices.length === 0 && (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Cargando servicios...</p>
                </div>
              )}

              {/* Services Grid */}
              {!loadingServices && userServices.length > 0 && (
                <>
                  {/* Botón para crear nuevo servicio (solo en tu perfil) */}
                  {isOwnProfile && (
                    <div className="flex justify-end mb-4">
                      <Button
                        variant="gradient"
                        size="sm"
                        onClick={() => setShowCreateServiceModal(true)}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Crear Servicio
                      </Button>
                    </div>
                  )}

                  {/* Grid de servicios - layout diferente al de posts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                    {userServices.map((service) => (
                      <ServiceCard
                        key={service.id}
                        service={service}
                        onClick={setSelectedService}
                      />
                    ))}
                  </div>

                  {/* Botón cargar más */}
                  {hasMoreServices && (
                    <div className="text-center mt-8">
                      <Button
                        variant="outline"
                        onClick={loadMoreServices}
                        loading={loadingServices}
                      >
                        Cargar más servicios
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Empty state */}
              {!loadingServices && userServices.length === 0 && (
                <div className="text-center py-16">
                  <div className="max-w-md mx-auto">
                    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {isOwnProfile ? 'No tienes servicios todavía' : 'No hay servicios disponibles'}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      {isOwnProfile 
                        ? 'Ofrece tus servicios profesionales a la comunidad' 
                        : 'Este usuario aún no ha publicado servicios'}
                    </p>
                    {isOwnProfile && (
                      <Button 
                        variant="gradient"
                        onClick={() => setShowCreateServiceModal(true)}
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Crear tu primer servicio
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'likes' && (
            <div>
              {loadingLiked && likedPosts.length === 0 && (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Cargando posts que te gustan...</p>
                </div>
              )}
              {!loadingLiked && likedPosts.length > 0 && (
                <MasonryGrid>
                  {likedPosts.map((post) => (
                    <PostCard
                      key={post.id}
                      post={post}
                    />
                  ))}
                </MasonryGrid>
              )}
              {!loadingLiked && likedPosts.length === 0 && (
                <div className="text-center py-16">
                  <div className="max-w-md mx-auto">
                    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-pink-100 to-red-100 dark:from-pink-900/30 dark:to-red-900/30 rounded-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-pink-600 dark:text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      No hay posts que te gusten
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Explora y da like a posts que te inspiren
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'saved' && (
            <div className="container-custom py-6">
              {loadingSaved ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
                </div>
              ) : savedError ? (
                <div className="text-center py-16">
                  <p className="text-red-600 dark:text-red-400">{savedError}</p>
                </div>
              ) : savedPosts.length === 0 && savedServices.length === 0 ? (
                <div className="text-center py-16">
                  <div className="max-w-md mx-auto">
                    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center">
                      <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      No tienes posts ni servicios guardados
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Guarda posts y servicios para verlos más tarde
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-10">
                  {savedPosts.length > 0 && (
                    <section>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Posts guardados</h2>
                      <MasonryGrid>
                        {savedPosts.map((post) => (
                          <PostCard
                            key={post.id}
                            post={post}
                            currentUserId={currentUser?.uid}
                            savedPostIds={currentUserData?.savedPosts || []}
                            onToggleSave={(postId) => {
                              setSavedPosts((prev) => prev.filter((p) => p.id !== postId));
                            }}
                          />
                        ))}
                      </MasonryGrid>
                    </section>
                  )}
                  {savedServices.length > 0 && (
                    <section>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Servicios guardados</h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {savedServices.map((service) => (
                          <ServiceCard
                            key={service.id}
                            service={service}
                            onClick={(s) => setSelectedService(s)}
                            currentUserId={currentUser?.uid}
                            savedServiceIds={currentUserData?.savedServices || []}
                            onToggleSaveService={(serviceId) => {
                              setSavedServices((prev) => prev.filter((s) => s.id !== serviceId));
                            }}
                          />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'portfolio' && (
            <Portfolio userId={userId} isOwnProfile={isOwnProfile} />
          )}
        </div>
      </div>
      </div>

      {/* Modal de Edición (solo si es tu perfil) */}
      {showEditModal && isOwnProfile && (
        <EditProfileModal
          userData={currentUserData}
          currentUser={currentUser}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Modal de Servicio */}
      {selectedService && (
        <ServiceModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
          onServiceDeleted={handleServiceDeleted}
          onServiceUpdated={handleServiceUpdated}
        />
      )}

      {/* Modal de Crear Servicio (solo si es tu perfil) */}
      {showCreateServiceModal && isOwnProfile && (
        <CreateServiceModal
          onClose={() => setShowCreateServiceModal(false)}
          onServiceCreated={handleServiceCreated}
        />
      )}

      {/* Modal de Seguidores/Siguiendo */}
      {showFollowListModal && (
        <FollowListModal
          isOpen={showFollowListModal}
          onClose={() => setShowFollowListModal(false)}
          userId={userId}
          type={followListType}
        />
      )}
    </Layout>
  );
};

export default ProfilePage;
